// app/frontend/components/EventForm/sections/DateTimeSection.tsx
import { useState, useRef } from 'react';
import { Accordion, Group, Text, SegmentedControl, Alert, ActionIcon } from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { IconCalendarTime, IconClock, IconAlertCircle } from '@tabler/icons-react';
import { DateTimeSectionProps } from '../types';
import RecurringEventFields from '../../RecurringEventFields';

const DateTimeSection: React.FC<DateTimeSectionProps> = ({ form, isDraft, isRecurring, setIsRecurring }) => {
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);

  const startPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => startTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const endPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => endTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value;
    form.setFieldValue('event_start_time', timeValue);
    form.validateField('event_end_date');
    form.validateField('event_end_time');
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value;
    form.setFieldValue('event_end_time', timeValue);
    form.validateField('event_end_date');
    form.validateField('event_end_time');
  };

  return (
    <Accordion multiple className="form-accordion">
      <Accordion.Item value="date-info">
        <Accordion.Control>
          <Group gap="xs">
            <IconCalendarTime size={28} />
            <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Date & Time Info</Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <SegmentedControl
            name="is_recurring"
            color="orange"
            transitionDuration={500}
            transitionTimingFunction="linear"
            size="sm"
            data={[
              { label: 'Single Event', value: 'false' },
              { label: 'Recurring Event', value: 'true' },
            ]}
            {...form.getInputProps('is_recurring')}
            style={{
              border: '2px solid orange',
              backgroundColor: 'white',
            }}
          />

          {isRecurring && <RecurringEventFields form={form} />}

          <div style={{ display: isRecurring ? 'none' : 'block' }}>
            <Group gap="md" mt="md" grow>
              <DatePickerInput
                label="Start Date"
                placeholder="Select date"
                name="event_start_date"
                value={
                  form.values.event_start_date
                    ? (() => {
                        // If the value is just a date string (YYYY-MM-DD), parse it properly in local timezone
                        if (form.values.event_start_date.length === 10 && form.values.event_start_date.includes('-')) {
                          // Create date at noon to avoid any timezone boundary issues
                          const [year, month, day] = form.values.event_start_date.split('-').map(Number);
                          const date = new Date(year, month - 1, day, 12, 0, 0);
                          return date;
                        } else {
                          // Otherwise, use the standard Date constructor
                          const date = new Date(form.values.event_start_date);
                          return date;
                        }
                      })()
                    : null
                }
                valueFormat="MMM DD, YYYY"
                onChange={(date) => {
                  if (date) {
                    const localDate = new Date(date);
                    localDate.setHours(0, 0, 0, 0);

                    // Get the date components directly from the local date object
                    const year = localDate.getFullYear();
                    const month = String(localDate.getMonth() + 1).padStart(2, '0');
                    const day = String(localDate.getDate()).padStart(2, '0');
                    const dateString = `${year}-${month}-${day}`;

                    // Store only the date portion, without time information
                    form.setFieldValue('event_start_date', dateString);

                    // Also copy the same date to the end date
                    form.setFieldValue('event_end_date', dateString);
                  } else {
                    form.setFieldValue('event_start_date', '');
                  }
                  form.validateField('event_end_date');
                  form.validateField('event_end_time');
                }}
                required={!isDraft}
                styles={{ label: { color: '#FFF7ED' } }}
              />

              <TimeInput
                label="Start time"
                name="event_start_time"
                ref={startTimeRef}
                rightSection={startPickerControl}
                color="orange"
                value={form.values.event_start_time}
                onChange={handleStartTimeChange}
                required={!isDraft}
                styles={{ label: { color: '#FFF7ED' } }}
              />
            </Group>

            <Group gap="md" mt="md" grow>
              <DatePickerInput
                label="End Date"
                name="event_end_date"
                placeholder="Select date"
                value={
                  form.values.event_end_date
                    ? (() => {
                        // If the value is just a date string (YYYY-MM-DD), parse it properly in local timezone
                        if (form.values.event_end_date.length === 10 && form.values.event_end_date.includes('-')) {
                          // Create date at noon to avoid any timezone boundary issues
                          const [year, month, day] = form.values.event_end_date.split('-').map(Number);
                          const date = new Date(year, month - 1, day, 12, 0, 0);
                          return date;
                        } else {
                          // Otherwise, use the standard Date constructor
                          const date = new Date(form.values.event_end_date);
                          return date;
                        }
                      })()
                    : null
                }
                valueFormat="MMM DD, YYYY"
                onChange={(date) => {
                  if (date) {
                    const localDate = new Date(date);
                    localDate.setHours(0, 0, 0, 0);

                    // Get the date components directly from the local date object
                    const year = localDate.getFullYear();
                    const month = String(localDate.getMonth() + 1).padStart(2, '0');
                    const day = String(localDate.getDate()).padStart(2, '0');
                    const dateString = `${year}-${month}-${day}`;

                    form.setFieldValue('event_end_date', dateString);
                  } else {
                    form.setFieldValue('event_end_date', '');
                  }
                  form.validateField('event_end_date');
                  form.validateField('event_end_time');
                }}
                required={!isDraft}
                styles={{ label: { color: '#FFF7ED' } }}
              />

              <TimeInput
                label="End time"
                name="event_end_time"
                ref={endTimeRef}
                rightSection={endPickerControl}
                color="orange"
                value={form.values.event_end_time}
                onChange={handleEndTimeChange}
                required={!isDraft}
                styles={{ label: { color: '#FFF7ED' } }}
              />
            </Group>
          </div>

          {(form.errors.event_end_date || form.errors.event_end_time) && (
            <Alert variant="filled" color="red" mt="md" icon={<IconAlertCircle size={16} />}>
              {form.errors.event_end_date && <div>{form.errors.event_end_date}</div>}
              {form.errors.event_end_time && <div>{form.errors.event_end_time}</div>}
            </Alert>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default DateTimeSection;
